import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface DocumentItem {
  id: string;
  codigo: string;
  recurso: string;
  unidad: string;
  cantidad: number;
  pUnitario: number;
  grupo: string;
  neto: number;
}

interface InvoiceData {
  ruc: string;
  emite: string;
  sucursal: string;
  cliente: string;
  fecha: string;
  periodo: string;
  moneda: string;
  tipoChange: string;
  numOrden: string;
  estado: string;
  proyecto: string;
  concepto: string;
  documentos: DocumentItem[];
  ordenVenta: string;
}

interface InvoiceModalProps {
  open: boolean;
  onClose: () => void;
  data: InvoiceData;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({ open, onClose, data }) => {
  // const [activeTab, setActiveTab] = useState(0);
  // const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
  //   setActiveTab(newValue);
  // };

  const LabelValue: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
          {`RUC ${data.ruc || '12345678'} - Factura ${'F001 - 00003956'}`}
        <Button
          onClick={onClose}
          size="small"
          sx={{
            minWidth: 'auto',
            p: 0.5,
          }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {/* Información General */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Emite" value={data.emite || 'Prueba emita'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Correlativo" value={data.emite || '0000000099'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="N°" value={data.emite || '0000000099'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Sucursal" value={data.sucursal || 'Prueba sucursal'} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelValue label="Cliente" value={data.cliente || 'prueba cliente'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Fecha" value={data.fecha || '11/11/2011'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Fecha Vencimiento" value={data.fecha || '11/11/2011'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Enviado a" value={data.fecha || 'usuarioprueba@prueba.com'} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelValue label="Período de aplicación" value={data.periodo || '05/12/2025 (202512)'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Tipo de cambio (no oficial)" value={data.tipoChange || '3.363'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Moneda" value={data.moneda || 'Dolares Americanos'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Forma de pago" value={data.moneda || 'Crédito a 30 días'} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelValue label="Estado" value={data.estado || 'Cobrado'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="N° Orden" value={data.numOrden || ''} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LabelValue label="Orden Venta N°" value={data.ordenVenta || '0000000|56'} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <LabelValue label="Proyecto" value={data.proyecto || '03133 - 1013 Proyecto Pardo'} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <LabelValue label="Revisado" value={data.proyecto || 'Si'} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <LabelValue label="Sistema Emisión Electrónica - SEE" value={data.proyecto || 'Si'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label="Concepto u Observación" value={data.concepto || 'Concepto u observacion de prueba'} />
            </Grid>
          </Grid>
        </Box>

        {/* Tab: Documento */}
        <TabPanel value={0} index={0}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recurso</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Unidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    P.U. sin Dcto. sin I.G.V.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Neto
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.documentos?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.codigo}</TableCell>
                    <TableCell>{row.recurso}</TableCell>
                    <TableCell align="right">{row.unidad}</TableCell>
                    <TableCell align="right">{row.cantidad.toFixed(4)}</TableCell>
                    <TableCell align="right">${row.pUnitario.toFixed(2)}</TableCell>
                    <TableCell align="right">${row.neto.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};